import { Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges } from '@angular/core'
import { Actions, ofType } from '@ngrx/effects'
import { Store, select } from '@ngrx/store'
import {
  Destroyable,
  ChannelNameTokens,
  Rental,
  selectRentalById,
  untilDestroy,
  Channel,
  isSomething,
  RatesChangedComplete,
  GroupRatesChangedComplete,
} from '@tokeet-frontend/tv3-platform'
import { Observable, merge, of } from 'rxjs'
import { catchError, debounceTime, filter, map, switchMap, take, tap } from 'rxjs/operators'
import {
  PushRatesToConnection,
  PushRatesToConnectionComplete,
  PushRatesToConnectionFailed,
} from '@tv3/store/connection/connection.actions'
import { Connection, isRecentlyLinked, stampedSinceLink } from '@tv3/store/connection/connection.model'
import { ConnectionHelperService } from '../../../connection-helper.service'
import { EditRateDialogService } from '@tv3/containers/rates/edit-rate-dialog/edit-rate-dialog.service'
import { selectConnectionById } from '@tv3/store/connection/connection.selectors'
import { ConnectionService } from '@tv3/store/connection/connection.service'
import { GetChannelRoomRatesPayload } from '@tv3/store/connection/connection.types'
import * as lodash from 'lodash'

// Lifecycle of the hands-off rate push that runs when this step becomes active.
// 'missing' and 'manual' are the two states that genuinely need the user.
type AutoPushRatesState = 'pushing' | 'done' | 'failed' | 'missing' | 'manual' | null

@Component({
  selector: 'app-bdc-listing-wizard-step2',
  templateUrl: './bdc-listing-wizard-step2.component.html',
  styleUrls: ['./bdc-listing-wizard-step2.component.scss'],
})
export class BDCListingWizardStep2Component extends Destroyable implements OnInit, OnChanges {
  @Input() connection: Connection
  @Input() channel: Channel
  // True while this step is the one selected in the stepper. The auto push must not
  // fire from ngOnInit: the stepper instantiates every step up front, and rates would
  // otherwise be pushed while the user is still linking on the first step.
  @Input() isActive = false
  rental: Rental

  @Output() prev = new EventEmitter()
  @Output() next = new EventEmitter()

  isRatesPushed = false
  isRentalRatesReady = true
  ratesMissing: string[] = []

  autoPushState: AutoPushRatesState = null
  autoPushedRateName = ''
  private autoPushAttempted = false

  constructor(
    private actions: Actions,
    private store: Store<any>,
    private connectionHelperService: ConnectionHelperService,
    private editRateDialog: EditRateDialogService,
    private connectionService: ConnectionService
  ) {
    super()
  }

  ngOnInit(): void {
    // A rate push recorded on the connection survives closing the wizard — unless the
    // stamp predates the current rental link: it then belongs to the previously linked
    // rental and the new rental's rates still need pushing.
    this.isRatesPushed = this.isRatesPushed || stampedSinceLink(this.connection, this.connection?.lastratepush)

    const rental$ = this.store.pipe(
      select(selectConnectionById, { id: this.connection.id }),
      // The entity can be briefly absent while a channel-scoped reload swaps the
      // connection store; an unguarded emission would read rentalId off undefined.
      filter(isSomething),
      tap((c) => {
        // A re-link while the wizard is open invalidates the pushed flag: the recorded
        // push belonged to the previously linked rental.
        if (c?.linkDate && this.connection?.linkDate && c.linkDate !== this.connection.linkDate) {
          this.isRatesPushed = false
          this.autoPushAttempted = false
        }
        this.connection = c
        this.isRatesPushed = this.isRatesPushed || stampedSinceLink(c, c?.lastratepush)
      }),
      switchMap((c) => this.store.pipe(select(selectRentalById(c.rentalId)))),
      tap((rental) => (this.rental = rental))
    )

    // Review-and-confirm: show what rate information is still missing, refreshed
    // whenever the rental or its rates change.
    merge(rental$, this.actions.pipe(ofType(RatesChangedComplete, GroupRatesChangedComplete)))
      .pipe(
        debounceTime(500),
        map(() => this.rental),
        filter(isSomething),
        switchMap((rental: Rental) => this.connectionHelperService.getRentalRatesReadiness(rental, this.connection)),
        untilDestroy(this)
      )
      .subscribe(({ ready, missing }) => {
        this.isRentalRatesReady = ready
        this.ratesMissing = missing
        // The user supplied what was missing: give the hands-off push another go.
        if (ready && this.autoPushState === 'missing') {
          this.autoPushAttempted = false
          this.maybeAutoPushRates()
        }
      })
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.isActive && this.isActive) {
      this.maybeAutoPushRates()
    }
  }

  // Pushes rates without a dialog when there is nothing to ask: the rental's rate data
  // is complete and Booking.com's default (Standard) rate plan can be resolved — the
  // exact values the Select Rate Category dialog would preselect. Anything short of
  // that hands over to the manual Push Rates flow below. Only a recently linked
  // connection is automated: reopening the wizard on an old, deliberately
  // availability-only connection must not overwrite its Booking.com prices.
  private maybeAutoPushRates() {
    if (!this.isActive || this.autoPushAttempted || this.isRatesPushed || !isRecentlyLinked(this.connection)) {
      return
    }
    this.autoPushAttempted = true

    this.store
      .pipe(
        select(selectConnectionById, { id: this.connection.id }),
        take(1),
        switchMap((connection) =>
          this.store.pipe(
            select(selectRentalById(connection.rentalId)),
            take(1),
            map((rental) => ({ connection, rental }))
          )
        ),
        switchMap(({ connection, rental }) => {
          if (!rental) {
            this.autoPushState = 'manual'
            return of(false)
          }
          return this.connectionHelperService.getRentalRatesReadiness(rental, connection).pipe(
            switchMap(({ ready }) => {
              if (!ready) {
                // The banner above the accordion lists what is missing.
                this.autoPushState = 'missing'
                return of(false)
              }
              return this.doAutoPushRates(connection)
            })
          )
        }),
        untilDestroy(this)
      )
      .subscribe()
  }

  private doAutoPushRates(connection: Connection): Observable<boolean> {
    this.autoPushState = 'pushing'

    const payload: GetChannelRoomRatesPayload = {
      api: ChannelNameTokens.BookingAPI,
      channel_id: connection.channelId,
      id: connection.roomId,
      propertyId: connection.propertyId,
    }

    return this.connectionService
      .getRoomRatesFromConnection(ChannelNameTokens.BookingAPI, connection.channelId, payload)
      .pipe(
        // Same default the Select Rate Category dialog preselects.
        map(
          (rates) => lodash.find(rates, (rate) => lodash.toLower(rate.name).includes('standard')) || lodash.first(rates)
        ),
        switchMap((channelRate) => {
          if (!channelRate) {
            this.autoPushState = 'manual'
            return of(false)
          }
          this.autoPushedRateName = channelRate.name
          this.store.dispatch(
            PushRatesToConnection({
              connId: connection.id,
              channelId: connection.channelId,
              channelName: this.channel.name,
              data: {
                category: 'default',
                rateid: channelRate.id,
                maxguest: channelRate.maxguest,
                pricemodel: channelRate.model,
                roomId: connection.roomId,
                propertyId: connection.propertyId,
                rental_id: connection.rentalId,
                maxPersons: channelRate.max_per,
                name: channelRate.name,
              },
            })
          )
          return this.actions.pipe(
            ofType(PushRatesToConnectionComplete, PushRatesToConnectionFailed),
            // Correlated both ways: another connection's push completing — or any unrelated
            // failure — must not be consumed as this push's result (BDC gets no optimistic
            // store stamp, so a stolen result would leave the wizard prompting a duplicate
            // manual push after a successful one).
            filter((action) =>
              action.type === PushRatesToConnectionFailed.type
                ? (action as any).connId === connection.id
                : (action as any).update?.id === connection.id
            ),
            take(1),
            map((action) => {
              const ok = action.type === PushRatesToConnectionComplete.type
              this.isRatesPushed = this.isRatesPushed || ok
              this.autoPushState = ok ? 'done' : 'failed'
              return ok
            })
          )
        }),
        catchError(() => {
          this.autoPushState = 'failed'
          return of(false)
        })
      )
  }

  onRetryAutoPushRates() {
    this.autoPushAttempted = false
    this.maybeAutoPushRates()
  }

  onAddStandardRate() {
    this.addRentalRate('standard')
  }

  onAddDynamicRate() {
    this.addRentalRate('dynamic')
  }

  onPushRates() {
    this.connectionHelperService
      .pushRatesForAPIConnection(this.channel, this.rental, this.connection)
      .pipe(
        switchMap(() =>
          this.actions.pipe(
            ofType(PushRatesToConnectionComplete),
            filter((action) => (action as any).update?.id === this.connection.id)
          )
        ),
        tap(() => {
          this.isRatesPushed = true
          this.autoPushState = 'done'
        }),
        take(1),
        untilDestroy(this)
      )
      .subscribe()
  }

  addRentalRate(type: 'standard' | 'dynamic') {
    this.editRateDialog.open({
      rentals: [this.rental],
      rental: this.rental,
      type,
    })
  }
}
